import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Typography,
    useTheme,
    useMediaQuery,
    TextField,
    Autocomplete,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import * as Lucide from 'lucide-react';
import axios from 'axios';

import LogoClaro from '../../assets/logo/logo-completo-fundo-claro.png';
import LogoEscuro from '../../assets/logo/logo-completa-fundo-escuro.png';
import LoginImage from '../../assets/image/imagem-de-abertura.svg';
import useAlturaTeclado from '../../hooks/useAlturaTeclado';

// O laboratorista entra no mesmo cliente todo dia e redigita o mesmo nome.
// Guardado por navegador; nada sensivel — e o identificador publico do tenant, o
// mesmo que ja aparece na URL.
const CHAVE_ULTIMO_TENANT = 'bioailab_crm_ultimo_tenant';

// localStorage LANCA em aba anonima ou com armazenamento bloqueado — sem o
// try/catch a tela de login inteira quebraria em vez de so perder a memoria.
const lerUltimoTenant = () => {
    try {
        return localStorage.getItem(CHAVE_ULTIMO_TENANT) || '';
    } catch {
        return '';
    }
};

const salvarUltimoTenant = (valor) => {
    try {
        localStorage.setItem(CHAVE_ULTIMO_TENANT, valor);
    } catch {
        /* segue sem memoria */
    }
};

const filterOptions = (options, { inputValue }) => {
    if (!inputValue) return [];
    return options.filter((o) =>
        o.toLowerCase().startsWith(inputValue.toLowerCase())
    );
};

const Login = () => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const isDark = theme.palette.mode === 'dark';
    const navigate = useNavigate();

    const [tenant, setTenant] = useState('');
    const [tenants, setTenants] = useState([]);
    const [carregandoTenants, setCarregandoTenants] = useState(true);
    const [falhaTenants, setFalhaTenants] = useState(false);

    // Decidido no mount: com um cliente lembrado, abrir o teclado de cara so
    // atrapalha — nao ha o que digitar, e ele cobre justamente o botao.
    const tinhaClienteSalvo = useRef(!!lerUltimoTenant());

    const carregarTenants = useCallback(() => {
        const base =
            process.env.REACT_APP_API_URL ?? 'http://localhost:8086/api';
        setCarregandoTenants(true);
        setFalhaTenants(false);
        axios
            .get(`${base}/tenants`)
            .then((res) => setTenants(res.data ?? []))
            .catch(() => {
                setTenants([]);
                setFalhaTenants(true);
            })
            .finally(() => setCarregandoTenants(false));
    }, []);

    useEffect(() => {
        carregarTenants();
    }, [carregarTenants]);

    // Preenche o cliente lembrado UMA VEZ, depois da lista chegar, e so se ele
    // ainda existir (um tenant renomeado nao pode ficar preso no campo).
    //
    // O `jaPreencheu` e o que impede o campo de se reescrever sozinho. Sem ele o
    // efeito dependeria de `tenant`, e ao apagar para digitar outro cliente ele
    // reaplicaria o salvo — tornando impossivel trocar de tenant.
    const jaPreencheu = useRef(false);
    useEffect(() => {
        if (jaPreencheu.current || carregandoTenants || falhaTenants) return;
        jaPreencheu.current = true;
        const salvo = lerUltimoTenant();
        if (salvo && tenants.includes(salvo)) setTenant(salvo);
    }, [carregandoTenants, falhaTenants, tenants]);

    const handleSubmit = useCallback(
        (e) => {
            e?.preventDefault();
            if (!tenant) return;
            salvarUltimoTenant(tenant);
            navigate(`/${tenant}`);
        },
        [tenant, navigate]
    );

    // Reservando embaixo o espaco que o teclado cobre, o conteudo tem para onde subir
    // — e o scroll no foco leva o formulario inteiro (campo E botao) para dentro dele.
    const formRef = useRef(null);
    const alturaTeclado = useAlturaTeclado();

    const aoFocarCliente = useCallback(() => {
        // O teclado leva ~300ms subindo; rolar antes usaria a altura antiga.
        window.setTimeout(() => {
            formRef.current?.scrollIntoView({
                block: 'end',
                behavior: 'smooth',
            });
        }, 350);
    }, []);

    const logo = isDark ? LogoEscuro : LogoClaro;

    const panelBg = isDark
        ? 'linear-gradient(160deg, #060D18 0%, #0D1B2E 60%, #122038 100%)'
        : `linear-gradient(160deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`;

    return (
        <Box
            sx={{
                // 100vh no mobile e a altura SEM o teclado — o navegador nao a
                // reduz quando ele abre. `dvh` encolhe junto; o vh fica de
                // fallback para navegador antigo.
                minHeight: '100vh',
                '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
                display: 'flex',
                bgcolor: 'background.default',
            }}
        >
            {/* ── Left panel: branding ── */}
            {isDesktop && (
                <Box
                    aria-hidden="true"
                    sx={{
                        width: '52%',
                        flexShrink: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        background: panelBg,
                        p: 8,
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {/* Decorative blobs */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -100,
                            right: -100,
                            width: 380,
                            height: 380,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255,255,255,0.045)',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: -80,
                            left: -80,
                            width: 280,
                            height: 280,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255,255,255,0.04)',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 140,
                            right: -50,
                            width: 150,
                            height: 150,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255,255,255,0.03)',
                        }}
                    />

                    <Box
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            textAlign: 'center',
                            maxWidth: 480,
                        }}
                    >
                        <img
                            src={LoginImage}
                            alt=""
                            style={{
                                width: '100%',
                                maxWidth: 440,
                                filter: 'drop-shadow(0 12px 40px rgba(0,0,0,0.3))',
                            }}
                        />
                    </Box>
                </Box>
            )}

            {/* ── Right panel: form ── */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    // Sem `justifyContent: center` de proposito: com o teclado
                    // aberto o conteudo fica mais alto que a area visivel, e
                    // centralizar em flex-column CORTA o topo (o overflow nao rola
                    // para tras). O `my: auto` do filho centraliza quando ha
                    // espaco e cede quando nao ha.
                    overflowY: 'auto',
                    p: { xs: 3, sm: 4, md: 6, lg: 8 },
                    // Espaco reservado para o teclado (0 quando fechado).
                    pb: alturaTeclado
                        ? `calc(${alturaTeclado}px + 24px)`
                        : undefined,
                    position: 'relative',
                }}
            >
                <Box
                    component="main"
                    sx={{
                        width: '100%',
                        maxWidth: { xs: '100%', sm: 380 },
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        my: 'auto',
                    }}
                >
                    {/* Logo */}
                    <Box sx={{ textAlign: 'center' }}>
                        <img
                            src={logo}
                            alt="BioAILab CRM"
                            style={{
                                maxWidth: 190,
                                height: 'auto',
                                display: 'inline-block',
                            }}
                        />
                    </Box>

                    {/* Heading */}
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography
                            variant="h6"
                            component="h1"
                            sx={{
                                fontWeight: 700,
                                letterSpacing: '-0.3px',
                                lineHeight: 1.3,
                            }}
                        >
                            Acesse sua conta
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.25, display: 'block' }}
                        >
                            Selecione o cliente para continuar.
                        </Typography>
                    </Box>

                    {/* Form */}
                    <Box
                        component="form"
                        ref={formRef}
                        onSubmit={handleSubmit}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                        }}
                    >
                        <Autocomplete
                            fullWidth
                            // No mobile o `small` (~40px) fica abaixo dos 48px
                            // recomendados de alvo de toque; `medium` da 56px.
                            size={isDesktop ? 'small' : 'medium'}
                            loading={carregandoTenants}
                            disabled={carregandoTenants || falhaTenants}
                            options={tenants}
                            filterOptions={filterOptions}
                            value={tenant || null}
                            onChange={(_, newValue) =>
                                setTenant(newValue || '')
                            }
                            noOptionsText="Nenhum cliente encontrado"
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Cliente"
                                    placeholder={
                                        carregandoTenants
                                            ? 'Carregando clientes...'
                                            : 'Digite para buscar...'
                                    }
                                    autoFocus={!tinhaClienteSalvo.current}
                                    onFocus={aoFocarCliente}
                                    autoComplete="off"
                                    error={falhaTenants}
                                    // Sem isto, uma API fora do ar produzia lista
                                    // vazia e o mesmo "Nenhum cliente encontrado"
                                    // de quem erra o nome — o usuario concluia que
                                    // a conta dele nao existe.
                                    helperText={
                                        falhaTenants
                                            ? 'Não foi possível carregar a lista de clientes.'
                                            : ' '
                                    }
                                />
                            )}
                        />

                        {falhaTenants && (
                            <Button
                                onClick={carregarTenants}
                                variant="outlined"
                                fullWidth
                                size={isDesktop ? 'medium' : 'large'}
                                startIcon={<Lucide.RefreshCw />}
                            >
                                Tentar novamente
                            </Button>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            size={isDesktop ? 'medium' : 'large'}
                            disabled={!tenant}
                            endIcon={<Lucide.ArrowRight />}
                            sx={{
                                fontWeight: 600,
                                letterSpacing: 0.3,
                                boxShadow: tenant
                                    ? `0 3px 10px ${alpha(theme.palette.primary.main, 0.35)}`
                                    : 'none',
                                transition: 'box-shadow 0.2s',
                            }}
                        >
                            Acessar sistema
                        </Button>
                    </Box>
                </Box>

                {/* Copyright footer */}
                <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{
                        // Ancorado no fundo so no desktop. No mobile ele passa a
                        // fluir depois do formulario: como `absolute` ele se
                        // prendia ao fundo da altura fantasma e cobria o botao
                        // quando o teclado subia.
                        textAlign: 'center',
                        px: 2,
                        mt: 3,
                        position: { xs: 'static', md: 'absolute' },
                        bottom: { md: 20 },
                    }}
                >
                    © {new Date().getFullYear()} BioAILab · Todos os direitos
                    reservados
                </Typography>
            </Box>
        </Box>
    );
};

export default Login;
